import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * PWA Install Prompt — friction #20.
 *
 * - Listens for beforeinstallprompt and stashes the event.
 * - Shows a dismissible banner (bottom-right on desktop, bottom-sheet on mobile)
 *   once the user has been signed in long enough to care.
 * - Remembers dismissal so we don't pester ("pwa_install_dismissed_at" in LS,
 *   re-prompt allowed after 30 days).
 * - Hides itself entirely on iOS standalone or once installed.
 */
const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const FIRST_PROMPT_DELAY_MS = 8000; // wait 8s after page load so it's not jarring

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Already running as PWA? Bail.
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Recently dismissed? Bail.
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    let timeoutId = null;
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      timeoutId = setTimeout(() => setShow(true), FIRST_PROMPT_DELAY_MS);
    };

    const installed = () => {
      setShow(false);
      setDeferredPrompt(null);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } else {
        // user said "no" — back off for a while
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } catch (e) {
      // ignore — some browsers throw if prompt called twice
    } finally {
      setDeferredPrompt(null);
      setShow(false);
    }
  };

  if (!show || !deferredPrompt) return null;

  return (
    <div
      className="fixed z-[60] bottom-20 md:bottom-6 right-3 md:right-6 left-3 md:left-auto max-w-sm bg-slate-900 text-white rounded-xl shadow-2xl ring-1 ring-amber-500/30 px-4 py-3 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="w-10 h-10 rounded-lg bg-amber-500 flex-shrink-0 flex items-center justify-center">
        <Download className="w-5 h-5 text-slate-900" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-base font-['Barlow_Condensed'] tracking-wide">Install FieldReady</p>
        <p className="text-xs text-slate-300 mt-0.5">
          Faster startup, push reminders, and works offline — straight from your home screen.
        </p>
        <div className="flex items-center gap-2 mt-2">
          <Button
            size="sm"
            className="h-8 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            onClick={install}
            data-testid="pwa-install-accept"
          >
            Install
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={dismiss}
            data-testid="pwa-install-later"
          >
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-slate-400 hover:text-white flex-shrink-0"
        aria-label="Dismiss"
        data-testid="pwa-install-close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
